import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useGallery,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Smart photo selection
// ----------------------------------------------------------------------------
// The three amenity cards (Pools, Fitness Center, Concierge) each want a
// photo that visually matches their topic. Rather than pin each card to a
// fixed gallery index — which works for a curated demo property but produces
// random/mismatched photos for any other property — we score every photo in
// `photo_gallery` against a short keyword list per card and pick the best
// match. Same pattern the TabbedCallouts section uses; kept self-contained
// here so the schema stays statically extractable.
//
// Editor overrides still win: the static `card<N>ImageDef` defaults below
// pin to concrete `photo_gallery[N]` indices so the contentSchema linter is
// satisfied, and at runtime we hand the slot group a derived definition
// whose `defaultIndex` reflects the smart pick. Once an editor swaps a
// photo, that override is what renders.
// ============================================================================

const CARD_KEYWORDS: Record<'card1' | 'card2' | 'card3', string[]> = {
  card1: [
    'pool', 'swim', 'swimming', 'jacuzzi', 'spa', 'hot tub', 'water',
    'aquatic', 'rooftop pool',
  ],
  card2: [
    'fitness', 'gym', 'exercise', 'workout', 'cardio', 'weight',
    'weights', 'yoga', 'training', 'wellness',
  ],
  card3: [
    'concierge', 'lobby', 'reception', 'clubhouse', 'lounge', 'entrance',
    'common', 'community', 'foyer', 'entryway',
  ],
};

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const searchText = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!searchText) return 0;
  return keywords.reduce((score, kw) => score + (searchText.includes(kw) ? 1 : 0), 0);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  cardKey: 'card1' | 'card2' | 'card3',
  usedIndices: Set<number>,
  fallbackIndex: number,
): number {
  const keywords = CARD_KEYWORDS[cardKey];
  if (!gallery.length) return fallbackIndex;

  let bestIndex = -1;
  let bestScore = 0;
  for (let i = 0; i < gallery.length; i++) {
    if (usedIndices.has(i)) continue;
    const score = scorePhoto(gallery[i], keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }
  if (bestIndex >= 0) return bestIndex;

  // No keyword hits — pick the first un-used index walking forward from
  // the fallback so the three cards still get distinct photos.
  for (let i = fallbackIndex; i < gallery.length; i++) {
    if (!usedIndices.has(i)) return i;
  }
  for (let i = 0; i < fallbackIndex; i++) {
    if (!usedIndices.has(i)) return i;
  }
  return Math.min(fallbackIndex, Math.max(gallery.length - 1, 0));
}

function useSmartCardPhotoIndices(): [number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const used = new Set<number>();
    const idx1 = findBestPhotoIndex(gallery, 'card1', used, 1);
    used.add(idx1);
    const idx2 = findBestPhotoIndex(gallery, 'card2', used, 3);
    used.add(idx2);
    const idx3 = findBestPhotoIndex(gallery, 'card3', used, 5);
    return [idx1, idx2, idx3];
  }, [gallery]);
}

function makeCardImageDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Static slot group definitions
// ----------------------------------------------------------------------------
// These are the literal definitions the schema linter extracts. Indices
// 1 / 3 / 5 stagger the three cards across the gallery so a fresh
// property lights up with distinct photos before the smart-selection
// pass kicks in. Runtime swaps in derived defs via `useMemo`.
// ============================================================================

const card1ImageDef: SlotGroupDefinition = {
  label: 'Card 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Main component
// ============================================================================

export function HomeAmenities() {
  // Smart photo selection picks each card a topic-matched photo from the
  // gallery; we then build a per-card slot-group definition that points
  // its sourceBinding at that index. Editor swaps still win because slot
  // group overrides flow through the runtime on top of these defaults.
  const [smartIdx1, smartIdx2, smartIdx3] = useSmartCardPhotoIndices();
  const card1ImageDynamicDef = useMemo(
    () => makeCardImageDef('Card 1 Image', smartIdx1),
    [smartIdx1],
  );
  const card2ImageDynamicDef = useMemo(
    () => makeCardImageDef('Card 2 Image', smartIdx2),
    [smartIdx2],
  );
  const card3ImageDynamicDef = useMemo(
    () => makeCardImageDef('Card 3 Image', smartIdx3),
    [smartIdx3],
  );

  const card1Image = useSlotGroup('homeamenities.card1_image', card1ImageDynamicDef);
  const card2Image = useSlotGroup('homeamenities.card2_image', card2ImageDynamicDef);
  const card3Image = useSlotGroup('homeamenities.card3_image', card3ImageDynamicDef);
  const cta = useSlotGroup('homeamenities.cta', ctaDef);

  // Per-card title tokens — promoted out of the link slot group's `label`
  // field into their own content tokens so editors get a dedicated
  // click-to-edit affordance directly on the text overlay instead of
  // having to drill into the slot group's edit panel. Authored as
  // richtext so editors can bold / italicize words ("Resort-Style
  // **Pools**") and embed field tokens. Defaults live in the
  // contentSchema below — runtime renders via <RichTextContent>.

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="home-amenities-section relative w-full overflow-hidden py-16 md:py-24">
      {/* Thin accent stripe pinned to the very top of the section — uses
          the primary brand color (the tan/Lakehaus accent that also
          fills the CTA below) so it reads as a deliberate horizontal
          rule against the secondary fill rather than disappearing into
          the same hue. */}
      <div className="home-amenities-top-stripe" aria-hidden="true" />

      <div
        ref={contentRef}
        className="reveal-fade-up relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        {/* ── Intro paragraph ── */}
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-14">
          <RichTextContent
            path="homeamenities.intro"
            label="Intro Paragraph"
            as="div"
            className="font-body text-sm md:text-base text-[var(--vibe-color-text-on-secondary)] leading-[1.92]"
          />
        </div>

        {/* ── Three amenity image cards ── */}
        <div className="home-amenities-cards grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-14">
          {/* Card 1 — Pools */}
          <div className="home-amenities-card">
            <EditableSlotGroup
              groupPath="homeamenities.card1_image"
              definition={card1ImageDynamicDef}
              className="home-amenities-card-image"
            >
              {card1Image.image ? (
                <VibesitesImage
                  url={card1Image.image}
                  alt={card1Image.alt || ''}
                  className="home-amenities-card-img"
                />
              ) : (
                <div className="home-amenities-card-placeholder" aria-hidden="true" />
              )}
            </EditableSlotGroup>
            <div className="home-amenities-card-overlay" aria-hidden="true" />
            <div className="home-amenities-card-link-wrap">
              <div className="home-amenities-card-link">
                <RichTextContent
                  path="homeamenities.card1_title"
                  label="Card 1 Title"
                  defaultValue="<p>Pools</p>"
                  as="span"
                  className="home-amenities-card-title"
                />
              </div>
            </div>
          </div>

          {/* Card 2 — Fitness Center */}
          <div className="home-amenities-card">
            <EditableSlotGroup
              groupPath="homeamenities.card2_image"
              definition={card2ImageDynamicDef}
              className="home-amenities-card-image"
            >
              {card2Image.image ? (
                <VibesitesImage
                  url={card2Image.image}
                  alt={card2Image.alt || ''}
                  className="home-amenities-card-img"
                />
              ) : (
                <div className="home-amenities-card-placeholder" aria-hidden="true" />
              )}
            </EditableSlotGroup>
            <div className="home-amenities-card-overlay" aria-hidden="true" />
            <div className="home-amenities-card-link-wrap">
              <div className="home-amenities-card-link">
                <RichTextContent
                  path="homeamenities.card2_title"
                  label="Card 2 Title"
                  defaultValue="<p>Fitness Center</p>"
                  as="span"
                  className="home-amenities-card-title"
                />
              </div>
            </div>
          </div>

          {/* Card 3 — Concierge */}
          <div className="home-amenities-card">
            <EditableSlotGroup
              groupPath="homeamenities.card3_image"
              definition={card3ImageDynamicDef}
              className="home-amenities-card-image"
            >
              {card3Image.image ? (
                <VibesitesImage
                  url={card3Image.image}
                  alt={card3Image.alt || ''}
                  className="home-amenities-card-img"
                />
              ) : (
                <div className="home-amenities-card-placeholder" aria-hidden="true" />
              )}
            </EditableSlotGroup>
            <div className="home-amenities-card-overlay" aria-hidden="true" />
            <div className="home-amenities-card-link-wrap">
              <div className="home-amenities-card-link">
                <RichTextContent
                  path="homeamenities.card3_title"
                  label="Card 3 Title"
                  defaultValue="<p>Concierge</p>"
                  as="span"
                  className="home-amenities-card-title"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="text-center">
          <EditableSlotGroup groupPath="homeamenities.cta" definition={ctaDef}>
            <a
              href={cta.url || '/amenities'}
              target={cta._target || '_self'}
              className="home-amenities-cta inline-flex items-center justify-center px-12 py-3.5 font-heading font-semibold text-sm uppercase tracking-[0.18em] transition-all duration-300"
            >
              {cta.label || 'View Amenities'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* ────────────────────────────────────────────────────────────
           Section background
           ────────────────────────────────────────────────────────────
           Lightened secondary fill: the brand's secondary color mixed
           with 25% white so the band reads as a softer, airier gray
           rather than the full-strength secondary. Mixing white in
           (rather than hardcoding a hue) keeps the section on-brand
           across palettes while still lifting it toward white. The
           white "on-secondary" text overlay stays legible because the
           mix only adds a modest amount of white. */
        .home-amenities-section {
          background: color-mix(in srgb, var(--vibe-color-secondary) 75%, #ffffff);
        }

        /* ────────────────────────────────────────────────────────────
           Top accent stripe
           ────────────────────────────────────────────────────────────
           5px primary-colored rule pinned to the very top edge of the
           section. Kept outside the scroll-reveal wrapper so it paints
           immediately as the section enters the viewport. */
        .home-amenities-top-stripe {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 5px;
          background: var(--vibe-color-primary);
          z-index: 2;
          pointer-events: none;
        }

        /* ────────────────────────────────────────────────────────────
           Amenity image cards
           ────────────────────────────────────────────────────────────
           Each card is a 4:3-ish photo wearing a soft dark gradient
           and a centered serif title in white. The cards are no longer
           links — they're static visual tiles, with navigation handled
           solely by the CTA below. A subtle scale-up on hover keeps the
           gallery feeling lively without implying clickability. */
        .home-amenities-card {
          position: relative;
          width: 100%;
          /* Skinny-and-wide on mobile — the grid stacks 1-up at this
             breakpoint, so a full-width 4:3 photo dominates the
             viewport. A letterbox 3:1 shape keeps each card full-bleed
             but short enough that all three fit comfortably above the
             CTA without scrolling. Reverts to 4:3 at md+ where the
             3-up row gives the cards back their portrait proportions. */
          aspect-ratio: 3 / 1;
          overflow: hidden;
          background: var(--vibe-color-surface, #f4f1ec);
          isolation: isolate;
        }
        @media (min-width: 768px) {
          .home-amenities-card {
            aspect-ratio: 4 / 3;
          }
        }
        .home-amenities-card-image {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .home-amenities-card-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: transform 0.6s ease;
        }
        .home-amenities-card:hover .home-amenities-card-img {
          transform: scale(1.04);
        }
        .home-amenities-card-placeholder {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 18%, transparent),
            color-mix(in srgb, var(--vibe-color-accent) 18%, transparent)
          );
        }

        /* Dark scrim — sits between the photo and the title so the
           white serif text stays legible against bright outdoor and
           interior photos alike. Deliberately gentle so the photo
           still reads as the focal element. */
        .home-amenities-card-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.20) 0%,
            rgba(0, 0, 0, 0.40) 100%
          );
          pointer-events: none;
        }

        .home-amenities-card-link-wrap {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
        }
        .home-amenities-card-link {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          padding: 1.5rem;
        }
        /* Card title sits one notch smaller than the previous pass —
           the reference comp was reading as oversized once the cards
           shrank into a 3-up row. The ladder roughly maps to
           text-xl → text-2xl → text-3xl with a tighter line-height
           so two-word titles like "Fitness Center" still fit on one
           line at the larger breakpoints. */
        .home-amenities-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: #ffffff;
          text-align: center;
          line-height: 1.15;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
        }
        @media (min-width: 768px) {
          .home-amenities-card-title { font-size: 1.5rem; }
        }
        @media (min-width: 1024px) {
          .home-amenities-card-title { font-size: 1.75rem; }
        }

        /* ────────────────────────────────────────────────────────────
           CTA — filled primary pill, no border
           ────────────────────────────────────────────────────────────
           Rests on the theme's primary brand color with the
           on-primary foreground; hover lightens the background
           slightly so the affordance still reads on top of the
           secondary section background without losing contrast. */
        .home-amenities-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #ffffff);
          border: none;
          border-radius: 2px;
        }
        .home-amenities-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 85%, #ffffff);
          color: var(--vibe-color-text-on-primary, #ffffff);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeAmenities',
  displayName: 'Home Amenities',
  tokens: {
    'homeamenities.intro': {
      label: 'Intro Paragraph',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue:
        '<p>Enjoy comfort and luxury, while taking in the beauty of life at our community. We bring you the latest amenities in the area, including resort-style spaces, a swimming pool, and pet-friendly retreats.</p>',
    },
    'homeamenities.card1_title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>Pools</p>',
    },
    'homeamenities.card2_title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Fitness Center</p>',
    },
    'homeamenities.card3_title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>Concierge</p>',
    },
  },
  slotGroups: {
    'homeamenities.card1_image': card1ImageDef,
    'homeamenities.card2_image': card2ImageDef,
    'homeamenities.card3_image': card3ImageDef,
    'homeamenities.cta': ctaDef,
  },
};

(HomeAmenities as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeAmenities;
